import { create } from 'zustand'
import type { Theme } from '@/lib/constants'
import { DEFAULT_THEME, STORAGE_KEYS, THEMES } from '@/lib/constants'

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return DEFAULT_THEME
  const v = localStorage.getItem(STORAGE_KEYS.THEME)
  // Validate — a corrupt/foreign value must not end up as a class on <html>.
  return (THEMES as readonly string[]).includes(v ?? '') ? (v as Theme) : DEFAULT_THEME
}

/**
 * The tenant default this device last resolved. See
 * `STORAGE_KEYS.ORG_DEFAULT_THEME` — only a stated side is ever stored, so
 * absence means «no white-label default in play», not «light».
 */
function getStoredOrgDefault(): 'light' | 'dark' | null {
  if (typeof window === 'undefined') return null
  const v = localStorage.getItem(STORAGE_KEYS.ORG_DEFAULT_THEME)
  return v === 'dark' || v === 'light' ? v : null
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  return theme === 'system' ? getSystemTheme() : theme
}

/**
 * Has this person ever CHOSEN a theme ON THIS DEVICE, as opposed to being
 * given one?
 *
 * Absence of the key is the signal, exactly as `profiles.language_preference`
 * being NULL means «never picked» rather than «picked Norwegian». It is what
 * lets an event theme set the DEFAULT appearance without overriding someone who
 * has stated a preference.
 *
 * ⚠️ ONLY a deliberate act may write this key. `profiles.theme_preference` is
 * `NOT NULL DEFAULT 'system'` (measured 11.08.2026), so EVERY signed-in profile
 * carries a value whether or not a human ever picked one — and while the
 * profile-sync path went through `setTheme`, that column default was copied
 * into localStorage on every login. The consequence was silent and total: every
 * signed-in viewer counted as «has chosen», so `lockMode` below could never
 * apply, and the documented precedence collapsed from
 * EXPLICIT CHOICE > EVENT LOCK > SYSTEM to EXPLICIT ALWAYS. Hydration goes
 * through `hydrateTheme`, which is why that function exists.
 */
export function hasExplicitThemeChoice(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(STORAGE_KEYS.THEME) !== null
}

/**
 * The ONE place that writes the theme class to <html>. Everything that wants
 * the class changed goes through this store — setTheme, the system-preference
 * listener (ThemeProvider), and the per-event theme locks. Scattered
 * classList writes were how the class, the store state, and an active event
 * theme ended up disagreeing (a toggle while an event theme was mounted, or an
 * OS flip, silently stomped whichever writer wasn't looking).
 */
function applyClass(mode: 'light' | 'dark') {
  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(mode)
}

interface ThemeState {
  /** Paper rendering is temporary and never changes a saved preference. */
  printing: boolean
  setPrinting: (printing: boolean) => void
  theme: Theme
  /** What the USER's preference resolves to, ignoring any event lock. */
  resolvedTheme: 'light' | 'dark'
  /**
   * What is ACTUALLY on `<html>` right now, after the lock has had its say.
   *
   * This is the value every renderer must read. `resolvedTheme` answers «what
   * did the person pick»; `effectiveMode` answers «what is the page», and when
   * an event lock is in force those two differ by design. Anything that PAINTS
   * from `resolvedTheme` while the class follows the lock renders one mode's
   * colours under the other mode's class — a light palette on a `.dark` page —
   * which is the white-on-white failure this subsystem keeps circling back to.
   */
  effectiveMode: 'light' | 'dark'
  /**
   * A per-event theme's forced mode. While set, the document class follows the
   * EVENT, and user/system changes update state without touching the class —
   * they take effect the moment the lock lifts. Set/cleared only by
   * useEventThemeOnDocument / usePublicEventBrand.
   */
  lockedMode: 'light' | 'dark' | null
  /**
   * The TENANT's default side (`brand_settings.default_theme`), for a viewer
   * signed in under a white-label brand.
   *
   * Sits one rung below the event lock and above the OS, so the precedence
   * reads EXPLICIT CHOICE > EVENT LOCK > ORG DEFAULT > SYSTEM. Both middle
   * tiers are BRAND DEFAULTS and rank by specificity: an event's own theme is
   * a statement about that event and beats the org-wide one.
   *
   * ⚠️ Like `lockedMode`, this must NEVER write localStorage. It is a default
   * imposed on someone who has not spoken, not a choice they made — and the
   * key that means «this person chose» is load-bearing for the tier above
   * (see `hasExplicitThemeChoice`).
   */
  orgDefaultMode: 'light' | 'dark' | null
  /** A deliberate act by the person. Persists the choice. */
  setTheme: (theme: Theme) => void
  /**
   * Adopt a preference the app already holds for this account (the profile
   * row), WITHOUT recording it as a choice made here. See
   * `hasExplicitThemeChoice`.
   */
  hydrateTheme: (theme: Theme) => void
  /** Re-resolve 'system' after an OS change (keeps resolvedTheme truthful). */
  syncSystemTheme: () => void
  /** Re-assert the current class (mount safety net after the pre-paint script). */
  applyCurrent: () => void
  lockMode: (mode: 'light' | 'dark') => void
  unlockMode: () => void
  /**
   * Adopt (or drop) the signed-in tenant's default side. Called by AppLayout as
   * the brand resolves — from the persisted snapshot before paint, then again
   * from the network. Repaints, because the brand arrives AFTER
   * `applyCurrent()` has already put the viewer's own mode on the document.
   */
  setOrgDefaultMode: (mode: 'light' | 'dark' | null) => void
}

export const useThemeStore = create<ThemeState>((set, get) => {
  /** Put a mode on the document and record it, so the two can never diverge. */
  const paint = (mode: 'light' | 'dark') => {
    const effectiveMode = get().printing ? 'light' : mode
    applyClass(effectiveMode)
    set({ effectiveMode })
  }

  /**
   * The mode the document should show, given the precedence:
   * EXPLICIT CHOICE > EVENT LOCK > ORG DEFAULT > SYSTEM.
   *
   * The two middle tiers are both BRAND DEFAULTS, ordered by specificity —
   * an event theme speaks for one event, a tenant default for the whole
   * account — and both yield whole to a person who has stated a side.
   */
  const modeFor = (
    resolved: 'light' | 'dark',
    lockedMode: 'light' | 'dark' | null,
    orgDefaultMode: 'light' | 'dark' | null,
  ) => (hasExplicitThemeChoice() ? resolved : (lockedMode ?? orgDefaultMode ?? resolved))

  const initial = getStoredTheme()
  const initialResolved = resolveTheme(initial)
  /**
   * Seeded from the device key, NOT left null until AppLayout resolves the
   * brand. The pre-paint script has already put the tenant's side on `<html>`
   * from this same key; booting the store at null would make `applyCurrent()`
   * repaint the viewer's OS side on mount and AppLayout flip it back a moment
   * later — a visible flash, in the one place that exists to prevent one.
   */
  const initialOrgDefault = getStoredOrgDefault()

  return {
    printing: false,
    setPrinting: (printing) => {
      set({ printing })
      get().applyCurrent()
    },
    theme: initial,
    resolvedTheme: initialResolved,
    effectiveMode: modeFor(initialResolved, null, initialOrgDefault),
    lockedMode: null,
    orgDefaultMode: initialOrgDefault,
    /**
     * The ONLY caller is a person pressing the theme control. So it applies its
     * own resolved mode and ignores `lockedMode` outright.
     *
     * ⚠️ THIS IS THE FIX FOR A DEAD BUTTON. Measured 10.08.2026 on
     * `/event/d987f6d9…` (a themed event), pressing «Bytt tema» twice:
     *
     *     start      class=dark  storage=light
     *     click 1    class=dark  storage=dark
     *     click 2    class=dark  storage=light
     *
     * The preference was written every single time and the class never moved,
     * because the event lock owned it. From the user's side the control was
     * simply broken — and worse, silently: no error, no feedback, the setting
     * genuinely changing underneath a screen that would not.
     *
     * The precedence is now EXPLICIT CHOICE > EVENT LOCK > SYSTEM, and that
     * ordering is deliberate rather than convenient. An event theme exists so a
     * branded event renders its identity, which is a DEFAULT — a reasonable thing
     * to impose on someone who has never expressed a preference. Light vs dark is
     * also a visual-comfort and accessibility need, and a brand must not be able
     * to trap a reader in a mode they cannot read. Branding sets the default;
     * the person sets the override.
     *
     * The event's PALETTE still applies either way — only the light/dark mode
     * follows the user — and both sides of it are contrast-derived (30b·1/2), so
     * a brand rendered in the other mode is still legible by construction.
     */
    setTheme: (theme) => {
      localStorage.setItem(STORAGE_KEYS.THEME, theme)
      const resolved = resolveTheme(theme)
      paint(resolved)
      set({ theme, resolvedTheme: resolved })
    },
    /**
     * Same state change, different MEANING: this is the account's stored
     * preference arriving from the database, not a person pressing anything.
     *
     * It writes localStorage only for a real stated side (`light`/`dark`), never
     * for `system` — because `system` is what the column defaults to for an
     * account that never chose, and copying a default into the key that means
     * «this person chose» is how the event lock died.
     */
    hydrateTheme: (theme) => {
      if (theme !== 'system') localStorage.setItem(STORAGE_KEYS.THEME, theme)
      const resolved = resolveTheme(theme)
      set({ theme, resolvedTheme: resolved })
      paint(modeFor(resolved, get().lockedMode, get().orgDefaultMode))
    },
    syncSystemTheme: () => {
      const { theme, lockedMode, orgDefaultMode } = get()
      if (theme !== 'system') return
      const resolved = getSystemTheme()
      set({ resolvedTheme: resolved })
      paint(modeFor(resolved, lockedMode, orgDefaultMode))
    },
    applyCurrent: () => {
      const { lockedMode, resolvedTheme, orgDefaultMode } = get()
      paint(modeFor(resolvedTheme, lockedMode, orgDefaultMode))
    },
    /**
     * An event theme's forced mode. It sets the DEFAULT appearance and yields to
     * a stated preference — `lockedMode` is still recorded either way, so
     * `unlockMode` restores correctly and a user who later clears their choice
     * gets the event's mode back.
     */
    lockMode: (mode) => {
      set({ lockedMode: mode })
      paint(modeFor(get().resolvedTheme, mode, get().orgDefaultMode))
    },
    /**
     * Leaving a branded event returns the viewer to the next tier down, which
     * for a white-label tenant is its own default — NOT straight to the OS.
     *
     * This used to paint `resolvedTheme` outright. That was correct while the
     * lock was the only default tier and is not any more: a Nortura admin
     * navigating out of a themed event would have dropped from the event's dark
     * to their system side, skipping the tenant default that owns every other
     * page of their app.
     */
    unlockMode: () => {
      set({ lockedMode: null })
      paint(modeFor(get().resolvedTheme, null, get().orgDefaultMode))
    },
    /**
     * The tenant default arriving from AppLayout. Repaints, because the brand
     * resolves after `applyCurrent()` has already painted the viewer's own side
     * — and clears to null when the resolved brand states nothing, so switching
     * from a white-label account to a personal one drops the default rather
     * than stranding it.
     */
    setOrgDefaultMode: (mode) => {
      if (get().orgDefaultMode === mode) return
      set({ orgDefaultMode: mode })
      paint(modeFor(get().resolvedTheme, get().lockedMode, mode))
    },
  }
})
